import { useEffect, useMemo } from "react";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod/v4";
import { useApiConfig, type ApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { roadsenseLivePayloadQuery, scopedRoadsenseLivePayloadQuery } from "@visioni/a3s-reports/app";
import {
  RoadSensePayload,
  RoadSenseLivePayloadResponse,
  ScopedRoadsenseLivePayloadResponse,
  getRoadsenseLivePayloadEndpoint,
  getScopedRoadsenseLivePayloadEndpoint,
} from "@visioni/a3s-reports/client-messages";
import { useTenant, useTenantId } from "@src/features/tenants";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import {
  isNoReportAccess,
  isReportRefusal,
  liveResponseError,
  RoadSenseReportNotFoundError,
  RoadSenseReportNotReadyError,
  visibleReportError,
} from "./roadsense-report-errors";

export { RoadSenseReportNotFoundError, RoadSenseReportNotReadyError } from "./roadsense-report-errors";

/**
 * RoadSense report fetch hook — an **independent** implementation, deliberately
 * not shared with `features/wastesense`.
 *
 * Reports are fetched by **`reportId`** (a ULID), not `periodStart`: RoadSense
 * reports are cumulative from an asset's hire start, so two reports can share a
 * `periodStart` and differ only in `periodEnd`. See the identity note in
 * `get-report.ts`.
 *
 * Reads the **live payload** route (`…/by-id/{reportId}/sites/{siteId}/live`),
 * which the server builds on request so the screen includes today's counts.
 * Behind `roadsense_camera_access` it reads the camera-scoped live route
 * (`…/roadsense/reports/{reportId}/sites/{siteId}/live`) instead, which builds
 * the payload from the caller's in-scope movements and carries no workbook.
 */

const STALE_TIME_MS = 60_000;

// The edge uploads traffic counts hourly, and every request re-reads the whole
// lifecycle from DynamoDB, so polling faster than every few minutes buys no
// fresher data and multiplies the read cost.
const POLL_INTERVAL_MS = 5 * 60_000;

/**
 * The live response with the payload left undecoded, so a payload that fails
 * the render schema still yields the workbook key (the screen offers the
 * download even when it can't render) — see `decodePayload`.
 */
const LiveEnvelope = z.object({
  data: RoadSenseLivePayloadResponse.shape.data.extend({ payload: z.unknown() }),
});

/** The scoped live response, likewise undecoded. Its workbook fields are sent to tenant admins only. */
const ScopedLiveEnvelope = z.object({
  data: ScopedRoadsenseLivePayloadResponse.shape.data.extend({ payload: z.unknown() }),
});

export interface UseRoadSenseReportResult {
  readonly payload: RoadSensePayload | undefined;
  /** ISO instant the server computed the payload at — the report's "last updated" time. */
  readonly asOf: string | undefined;
  /**
   * This site's `.xlsx` key; `undefined` when the report has no workbook yet,
   * and on the scoped route for anyone but a tenant admin.
   */
  readonly workbookS3Key: string | undefined;
  /**
   * The last day the stored `.xlsx` covers (`YYYY-MM-DD`). The live report runs
   * to today, but the workbook is only rebuilt by the daily run, so it lags.
   * `undefined` when unknown (no workbook, or one being rebuilt).
   */
  readonly workbookPeriodEnd: string | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  /** The camera-scoped live route answered 403: the caller has no access in the tenant. Always false with the flag off. */
  readonly hasNoReportAccess: boolean;
  readonly isTenantPending: boolean;
}

/**
 * Fetches and polls a single RoadSense **site's** live report by
 * `(reportId, siteId)`, decoding its payload at the boundary.
 */
export function useRoadSenseReport(reportId: string | undefined, siteId: string | undefined): UseRoadSenseReportResult {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();
  const { isLoading: isTenantLoading } = useTenant();
  const cameraScoped = useBooleanFlag(FEATURE_FLAGS.ROADSENSE_CAMERA_ACCESS);

  // Reuse the endpoint's query key, but run our own queryFn to surface the typed
  // 404/409. Each route has its own key, so a flag flip never reads the
  // other route's cached payload.
  const queryKey = useMemo(
    () =>
      reportId === undefined || siteId === undefined ? []
      : cameraScoped ? scopedRoadsenseLivePayloadQuery(apiConfig, { reportId, siteId }).queryKey
      : roadsenseLivePayloadQuery(apiConfig, { type: "roadsense", reportId, siteId }).queryKey,
    [apiConfig, cameraScoped, reportId, siteId],
  );

  const enabled = !!tenantId && reportId !== undefined && siteId !== undefined;

  const query = useQuery({
    queryKey,
    queryFn:
      apiConfig.tenant && reportId !== undefined && siteId !== undefined ?
        ({ signal }) =>
          cameraScoped ?
            fetchScopedLivePayload(apiConfig, reportId, siteId, signal)
          : fetchLivePayload(apiConfig, reportId, siteId, signal)
      : skipToken,
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Polls through a 409 too: a report whose first generation is still
    // running becomes available without a reload.
    // On the scoped route a 403 means no access in the tenant, which polling
    // won't change, so it stops polling until the next focus or mount.
    refetchInterval:
      cameraScoped ? (q) => (isNoReportAccess(q.state.error) ? false : POLL_INTERVAL_MS) : POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => {
      // 404/409 are terminal for this attempt (the next poll asks again).
      // Other errors keep the default backoff (3 attempts).
      if (error instanceof RoadSenseReportNotFoundError) return false;
      if (error instanceof RoadSenseReportNotReadyError) return false;
      if (cameraScoped && isNoReportAccess(error)) return false;
      return failureCount < 3;
    },
  });

  // A refusal (see `isReportRefusal`) means the user may no longer see this
  // report, so a cached one, including one restored from the persisted cache
  // on a fresh load, must not stay on screen.
  const refused = isReportRefusal(query.error, { cameraScoped });
  const data = refused ? undefined : query.data;
  const hasRefusedReportCached = refused && query.data !== undefined;
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!hasRefusedReportCached) return;
    // Drop the refused payload so the persister stops saving it (it keeps an
    // errored query's data). `setState` only notifies observers; it doesn't
    // fetch. The next render sees no data, so this runs once per refusal.
    queryClient.getQueryCache().find({ queryKey, exact: true })?.setState({ data: undefined, dataUpdatedAt: 0 });
  }, [hasRefusedReportCached, queryClient, queryKey]);
  const error = visibleReportError({ hasReport: data !== undefined, error: query.error, cameraScoped });

  return {
    payload: data ? decodePayload(data.data.payload) : undefined,
    asOf: data?.data.asOf,
    workbookS3Key: data?.data.workbookS3Key,
    workbookPeriodEnd: data?.data.workbookPeriodEnd,
    isLoading: query.isLoading,
    isError: error !== null,
    error,
    hasNoReportAccess: cameraScoped && isNoReportAccess(error),
    isTenantPending: isTenantLoading || !tenantId,
  };
}

async function fetchLivePayload(
  apiConfig: ApiConfig,
  reportId: string,
  siteId: string,
  signal: AbortSignal | undefined,
): Promise<z.output<typeof LiveEnvelope>> {
  const { tenant, authenticatedFetch } = apiConfig;
  if (!tenant) {
    // Unreachable in practice (gated by `enabled`/`skipToken`); the type system
    // doesn't know that.
    throw new Error("Tenant not configured");
  }
  const path = expandEndpoint(getRoadsenseLivePayloadEndpoint, {
    path: { tenantId: tenant.tenantId, type: "roadsense", reportId, siteId },
  });
  const response = await authenticatedFetch(`${tenant.apiBaseUrl}${path}`, signal ? { signal } : {});
  const error = await liveResponseError(response);
  if (error !== undefined) throw error;
  const body: unknown = await response.json();
  return LiveEnvelope.parse(body);
}

async function fetchScopedLivePayload(
  apiConfig: ApiConfig,
  reportId: string,
  siteId: string,
  signal: AbortSignal | undefined,
): Promise<z.output<typeof LiveEnvelope>> {
  const { tenant, authenticatedFetch } = apiConfig;
  if (!tenant) throw new Error("Tenant not configured");
  const path = expandEndpoint(getScopedRoadsenseLivePayloadEndpoint, {
    path: { tenantId: tenant.tenantId, reportId, siteId },
  });
  // A 403 never gets here: `authenticatedFetch` throws it as a `ForbiddenError`,
  // left as the query's error (see `isNoReportAccess`).
  const response = await authenticatedFetch(`${tenant.apiBaseUrl}${path}`, signal ? { signal } : {});
  const error = await liveResponseError(response);
  if (error !== undefined) throw error;
  const body: unknown = await response.json();
  return ScopedLiveEnvelope.parse(body);
}

function decodePayload(payload: unknown): RoadSensePayload | undefined {
  const result = RoadSensePayload.safeParse(payload);
  if (result.success) return result.data;
  // The server validates the payload it builds, so this only fires on schema
  // drift between the deployed API and this client. Returning `undefined` lets
  // the renderer show an explicit "payload schema mismatch" state rather than
  // crashing.
  console.error("RoadSensePayload schema mismatch", result.error);
  return undefined;
}
